import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DataProvider } from "@/contexts/DataContext";
import AppBootLoader from "@/components/app/AppBootLoader";
import { AppLayout } from "@/components/layout/AppLayout";
import RequireAuth from "@/components/auth/RequireAuth";
import PwaInstallBanner from "@/components/PwaInstallBanner";
import PwaNotificationReminder from "@/components/PwaNotificationReminder";
import PwaPushNotificationBootstrapper from "@/components/PwaPushNotificationBootstrapper";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ChangelogProvider } from "@/contexts/ChangelogContext";
import { ChangelogModal } from "@/components/changelog/ChangelogModal";
import ArrowFormNavigator from "@/components/ArrowFormNavigator";

const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const InvitePasswordPage = lazy(() => import("@/pages/InvitePasswordPage"));
const AgeDeclarationPage = lazy(() => import("@/pages/AgeDeclarationPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CommunitiesPage = lazy(() => import("@/pages/CommunitiesPage"));
const PaymentsPage = lazy(() => import("@/pages/PaymentsPage"));
const IncidentsPage = lazy(() => import("@/pages/IncidentsPage"));
const MeetingsPage = lazy(() => import("@/pages/MeetingsPage"));
const MeetingAttendeesPage = lazy(() => import("@/pages/MeetingAttendeesPage"));
const MeetingAgendaOrderPage = lazy(() => import("@/pages/MeetingAgendaOrderPage"));
const DocumentsPage = lazy(() => import("@/pages/DocumentsPage"));
const ReservationsPage = lazy(() => import("@/pages/ReservationsPage"));
const ReservationPaymentCancelledPage = lazy(() => import("@/pages/ReservationPaymentCancelledPage"));
const ReservationPaymentSuccessPage = lazy(() => import("@/pages/ReservationPaymentSuccessPage"));
const ProvidersPage = lazy(() => import("@/pages/ProvidersPage"));
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const SuggestionsPage = lazy(() => import("@/pages/SuggestionsPage"));
const AnnouncementsPage = lazy(() => import("@/pages/AnnouncementsPage"));
const QuickPollsPage = lazy(() => import("@/pages/QuickPollsPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const RolesPage = lazy(() => import("@/pages/RolesPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const AdminCommunitiesPage = lazy(() => import("@/pages/AdminCommunitiesPage"));
const AdminCampaignsPage = lazy(() => import("@/pages/AdminCampaignsPage"));
const AdminAuditLogsPage = lazy(() => import("@/pages/AdminAuditLogsPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const MaintenancePage = lazy(() => import("@/pages/MaintenancePage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsAndConditionsPage = lazy(() => import("@/pages/TermsAndConditionsPage"));
const LegalNoticePage = lazy(() => import("@/pages/LegalNoticePage"));
const CookiePolicyPage = lazy(() => import("@/pages/CookiePolicyPage"));

interface PageRoute {
  path?: string;
  index?: boolean;
  element: ReactNode;
}

interface RedirectRoute {
  from: string;
  to: string;
}

const queryClient = new QueryClient();
const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === "true";

const publicRoutes: PageRoute[] = [
  { path: "/", element: <LandingPage /> },
  { path: "/legal-notice", element: <LegalNoticePage /> },
  { path: "/privacy-policy", element: <PrivacyPolicyPage /> },
  { path: "/terms-and-conditions", element: <TermsAndConditionsPage /> },
  { path: "/cookie-policy", element: <CookiePolicyPage /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/register", element: <RegisterPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/invite-password", element: <InvitePasswordPage /> },
  { path: "/age-declaration", element: <AgeDeclarationPage /> },
];

const protectedRoutes: PageRoute[] = [
  { index: true, element: <Dashboard /> },
  { path: "communities", element: <CommunitiesPage /> },
  { path: "payments", element: <PaymentsPage /> },
  { path: "incidents", element: <IncidentsPage /> },
  { path: "meetings", element: <MeetingsPage /> },
  { path: "meetings/:meetingId/attendees", element: <MeetingAttendeesPage /> },
  { path: "meetings/:meetingId/order", element: <MeetingAgendaOrderPage /> },
  { path: "documents", element: <DocumentsPage /> },
  { path: "reservations", element: <ReservationsPage /> },
  { path: "reservations/payment-success", element: <ReservationPaymentSuccessPage /> },
  { path: "reservations/payment-cancelled", element: <ReservationPaymentCancelledPage /> },
  { path: "providers", element: <ProvidersPage /> },
  { path: "chat", element: <ChatPage /> },
  { path: "suggestions", element: <SuggestionsPage /> },
  { path: "announcements", element: <AnnouncementsPage /> },
  { path: "quick-polls", element: <QuickPollsPage /> },
  { path: "support", element: <SupportPage /> },
  { path: "settings", element: <SettingsPage /> },
  { path: "profile", element: <ProfilePage /> },
  { path: "roles", element: <RolesPage /> },
  { path: "calendar", element: <CalendarPage /> },
  { path: "users", element: <UsersPage /> },
  { path: "admin/communities", element: <AdminCommunitiesPage /> },
  { path: "admin/campaigns", element: <AdminCampaignsPage /> },
  { path: "admin/audit-logs", element: <AdminAuditLogsPage /> },
  { path: "admin/dashboard", element: <AdminDashboardPage /> },
];

const legacyRedirects: RedirectRoute[] = [
  { from: "/dashboard", to: "/app" },
  { from: "/communities", to: "/app/communities" },
  { from: "/payments", to: "/app/payments" },
  { from: "/incidents", to: "/app/incidents" },
  { from: "/meetings", to: "/app/meetings" },
  { from: "/documents", to: "/app/documents" },
  { from: "/reservations", to: "/app/reservations" },
  { from: "/providers", to: "/app/providers" },
  { from: "/chat", to: "/app/chat" },
  { from: "/suggestions", to: "/app/suggestions" },
  { from: "/announcements", to: "/app/announcements" },
  { from: "/quick-polls", to: "/app/quick-polls" },
  { from: "/settings", to: "/app/settings" },
  { from: "/profile", to: "/app/profile" },
  { from: "/roles", to: "/app/roles" },
  { from: "/admin/dashboard", to: "/app/admin/dashboard" },
  { from: "/calendar", to: "/app/calendar" },
  { from: "/users", to: "/app/users" },
];

const renderPageRoute = ({ element, index, path }: PageRoute) => (
  <Route key={path ?? "index"} index={index} path={path} element={element} />
);

const AGE_DECLARATION_ALLOWED_PATHS = new Set([
  "/age-declaration",
  "/privacy-policy",
  "/terms-and-conditions",
  "/legal-notice",
  "/cookie-policy",
]);

const PendingAgeDeclarationRedirect = () => {
  const { currentUser, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || currentUser?.status !== "pending_age_declaration") {
      return;
    }

    if (AGE_DECLARATION_ALLOWED_PATHS.has(location.pathname)) {
      return;
    }

    navigate("/age-declaration", { replace: true, state: { from: location } });
  }, [currentUser?.status, isLoading, location, navigate]);

  return null;
};

const AppRuntime = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const isLandingPage = pathname === "/";
  const isAgeDeclarationPage = pathname === "/age-declaration";

  return (
    <div className="app-surface">
      <div className="app-surface-layer" aria-hidden="true" />
      <div className="app-surface-layer" aria-hidden="true" />
      <div className="relative z-10 flex min-h-[var(--app-dvh)] flex-col">
        <Toaster />
        <Sonner />
        <PendingAgeDeclarationRedirect />
        <ArrowFormNavigator />
        {!isAgeDeclarationPage && <PwaInstallBanner />}
        {!isLandingPage && !isAgeDeclarationPage && (
          <>
            <PwaNotificationReminder />
            <PwaPushNotificationBootstrapper />
            <ChangelogModal />
          </>
        )}
        <Suspense fallback={<AppBootLoader />}>
          {children}
        </Suspense>
      </div>
    </div>
  );
};

const AppShell = ({ children }: { children: ReactNode }) => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <DataProvider>
            <ChangelogProvider>
              <BrowserRouter>
                <AppRuntime>{children}</AppRuntime>
              </BrowserRouter>
            </ChangelogProvider>
          </DataProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

const AppRoutes = () => {
  if (isMaintenanceMode) {
    return (
      <Routes>
        <Route path="/maintenance" element={<MaintenancePage />} />
        <Route path="*" element={<Navigate to="/maintenance" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {publicRoutes.map(renderPageRoute)}

      <Route
        path="/app"
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        {protectedRoutes.map(renderPageRoute)}
        <Route path="polls" element={<Navigate to="/app/meetings" replace />} />
      </Route>

      {legacyRedirects.map(({ from, to }) => (
        <Route key={from} path={from} element={<Navigate to={to} replace />} />
      ))}

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <AppShell>
    <AppRoutes />
  </AppShell>
);

export default App;
